import { useEffect, useState } from 'react';
import { playlists, packages, type Playlist, type Package } from '../../api';

export function PlaylistsPage() {
  const [list, setList] = useState<Playlist[]>([]);
  const [pkgs, setPkgs] = useState<Package[]>([]);
  const [editing, setEditing] = useState<Playlist | 'new' | null>(null);

  async function refresh() {
    const [pl, pk] = await Promise.all([playlists.list(), packages.list()]);
    setList(pl.playlists);
    setPkgs(pk.packages);
  }
  useEffect(() => { refresh(); }, []);

  async function remove(id: string) {
    if (!confirm('Delete this playlist?')) return;
    await playlists.remove(id);
    refresh();
  }

  return (
    <div>
      <div className="toolbar">
        <h1>Playlists</h1>
        <div className="spacer" />
        <button className="primary" onClick={() => setEditing('new')}>+ New playlist</button>
      </div>
      {list.length === 0 ? (
        <div className="empty">No playlists yet.</div>
      ) : (
        <table>
          <thead><tr><th>Name</th><th>Items</th><th>Total duration</th><th></th></tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id}>
                <td><strong>{p.name}</strong></td>
                <td>{p.items.length}</td>
                <td>{fmtDuration(p.items.reduce((a, i) => a + i.duration_s, 0))}</td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => setEditing(p)}>Edit</button>{' '}
                  <button className="danger" onClick={() => remove(p.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <PlaylistEditor
          playlist={editing === 'new' ? null : editing}
          packages={pkgs}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function fmtDuration(s: number) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h ? `${h}h` : '', m ? `${m}m` : '', `${sec}s`].filter(Boolean).join(' ');
}

function PlaylistEditor({
  playlist, packages: pkgs, onClose, onSaved,
}: { playlist: Playlist | null; packages: Package[]; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(playlist?.name ?? '');
  const [items, setItems] = useState(playlist?.items ?? []);
  const [busy, setBusy] = useState(false);

  // Flatten all versions across packages and remember each version's full duration
  const versions = pkgs.flatMap((p) =>
    (p.versions ?? []).map((v) => ({
      id: v.id,
      label: `${p.name} v${v.version}`,
      total_duration_s: v.total_duration_s ?? null,
    }))
  );
  const versionById = new Map(versions.map((v) => [v.id, v]));

  function addItem() {
    if (versions.length === 0) return;
    const v0 = versions[0];
    setItems([...items, {
      package_version_id: v0.id,
      duration_s: v0.total_duration_s && v0.total_duration_s > 0 ? v0.total_duration_s : 10,
    }]);
  }
  function updateItem(i: number, patch: Partial<{ package_version_id: string; duration_s: number }>) {
    setItems(items.map((it, idx) => {
      if (idx !== i) return it;
      // When the user picks a different version, auto-update the duration to
      // that version's total scene duration (so multi-scene packages default
      // to one full cycle). The user can still override afterwards.
      if (patch.package_version_id && patch.package_version_id !== it.package_version_id) {
        const newV = versionById.get(patch.package_version_id);
        const auto = newV?.total_duration_s;
        return {
          ...it,
          package_version_id: patch.package_version_id,
          duration_s: auto && auto > 0 ? auto : it.duration_s,
        };
      }
      return { ...it, ...patch };
    }));
  }
  function removeItem(i: number) { setItems(items.filter((_, idx) => idx !== i)); }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setItems(copy);
  }

  async function save() {
    setBusy(true);
    try {
      if (playlist) {
        await playlists.update(playlist.id, { name, items });
      } else {
        await playlists.create({ name, items });
      }
      onSaved();
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 640 }}>
        <h2 style={{ marginTop: 0 }}>{playlist ? 'Edit playlist' : 'New playlist'}</h2>
        <div className="field"><label>Name</label><input value={name} onChange={(e) => setName(e.target.value)} autoFocus /></div>
        <label>Items (top to bottom)</label>
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '0.6em', minHeight: 80 }}>
          {items.length === 0 && <div style={{ color: 'var(--fg-3)', textAlign: 'center', padding: '0.6em' }}>No items yet.</div>}
          {items.map((it, i) => {
            const v = versionById.get(it.package_version_id);
            const full = v?.total_duration_s ?? null;
            return (
              <div key={i} style={{ display: 'flex', gap: '0.5em', alignItems: 'center', marginBottom: '0.4em' }}>
                <select value={it.package_version_id} onChange={(e) => updateItem(i, { package_version_id: e.target.value })} style={{ flex: 1 }}>
                  {versions.map((vv) => <option key={vv.id} value={vv.id}>{vv.label}</option>)}
                </select>
                <input type="number" min={1} max={86400} value={it.duration_s}
                  onChange={(e) => updateItem(i, { duration_s: Number(e.target.value) })}
                  style={{ width: 90 }}
                  title={full ? `Package full duration: ${full}s` : undefined} />
                <span style={{ color: 'var(--fg-3)' }}>s</span>
                {full && full !== it.duration_s && (
                  <button onClick={() => updateItem(i, { duration_s: full })}
                    title={`Set to package full duration (${full}s)`}
                    style={{ padding: '2px 8px', fontSize: '0.78em' }}>
                    ↺ {full}s
                  </button>
                )}
                <button onClick={() => move(i, -1)} disabled={i === 0}>↑</button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1}>↓</button>
                <button className="danger" onClick={() => removeItem(i)}>×</button>
              </div>
            );
          })}
        </div>
        <button onClick={addItem} disabled={versions.length === 0} style={{ marginTop: '0.6em' }}>
          + Add item
        </button>
        {versions.length === 0 && <div className="error">Upload at least one package version first.</div>}

        <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'flex-end', marginTop: '1.2em' }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={!name || busy} onClick={save}>
            {busy ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
